import {CommonModule} from '@angular/common';
import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {
  AgendaItemCommentResponse,
  EvaluationMatrixRowResponse,
  ParticipatingToolResponse,
  UserEvaluationRequest,
  UserEvaluationResponse,
} from '@frontend/shared-api';
import {DirectusTranslationPipe} from '@frontend/directus';
import {ButtonModule} from 'primeng/button';
import {TooltipModule} from 'primeng/tooltip';
import {RatingInputComponent} from '../rating-input/rating-input.component';
import {EnrichedEvaluationDataResponse} from '../../evaluation-models';

@Component({
  selector: 'app-evaluation-matrix',
  imports: [
    CommonModule,
    TranslocoPipe,
    RatingInputComponent,
    TooltipModule,
    ButtonModule,
    DirectusTranslationPipe,
  ],
  templateUrl: './evaluation-matrix.component.html',
  styleUrls: ['./evaluation-matrix.component.scss'],
})
export class EvaluationMatrixComponent {
  tool = input.required<ParticipatingToolResponse>();
  data = input.required<EnrichedEvaluationDataResponse>();
  selectedUserId = input<number | null>(null);
  averageMode = input(false);
  averageUserIds = input<number[]>([]);
  currentUserId = input<number | null | undefined>(null);
  readOnly = input(false);

  ratingChange = output<UserEvaluationRequest>();
  commentOpen = output<number>();

  protected userRatings = signal<Map<string, number>>(new Map());
  protected hasComments = signal<Set<number>>(new Set());
  private readonly transloco = inject(TranslocoService);
  protected readonly currentLang = computed(() => this.transloco.getActiveLang());
  private readonly updateEffect = effect(() => {
    this.updateView();
  });

  private updateView() {
    const data = this.data();
    const tool = this.tool();
    const ratingsMap = new Map<string, number>();
    const commentSet = new Set<number>();

    const matrixRowById = new Map<number, EvaluationMatrixRowResponse>();
    for (const row of data.matrixRows ?? []) {
      if (row.id == null) continue;
      matrixRowById.set(row.id, row);
    }

    if (this.averageMode()) {
      const eligibleUserIds = new Set<number>(this.averageUserIds() ?? []);

      // Sum/count per matrix cell key
      const sums = new Map<string, {sum: number; count: number}>();

      (data.userEvaluations ?? []).forEach((e: UserEvaluationResponse) => {
        const participatingToolId = e.participatingToolId;
        if (participatingToolId == null || participatingToolId !== tool.id) return;

        const userId = e.userId;
        if (userId == null || !eligibleUserIds.has(userId)) return;
        // `null` means "uninitialized" and should not influence the average.
        // `-1` means "No Rating" and should not influence the average either.
        // `0` means "not seen" and IS a real value that should be averaged.
        if (e.rating == null || e.rating === -1) return;

        const matrixId = e.matrixId;
        if (matrixId == null) return;

        const matrixRow = matrixRowById.get(matrixId);
        if (!matrixRow) return;

        const agendaItemId = matrixRow.demonstrationAgendaItemId;
        const subcriteriaId = matrixRow.subcriteriaDirectusId;
        if (agendaItemId == null || !subcriteriaId) return;

        const key = `${agendaItemId}-${subcriteriaId}`;
        const current = sums.get(key) ?? {sum: 0, count: 0};
        sums.set(key, {sum: current.sum + e.rating, count: current.count + 1});
      });

      sums.forEach((v, key) => {
        if (v.count > 0) {
          ratingsMap.set(key, v.sum / v.count);
        }
      });
    } else {
      const targetUserId = this.selectedUserId() ?? this.currentUserId();
      if (targetUserId != null) {
        const userEvals = (data.userEvaluations ?? []).filter(
          (e: UserEvaluationResponse) =>
            e.userId === targetUserId && e.participatingToolId === tool.id,
        );

        userEvals.forEach((e: UserEvaluationResponse) => {
          const matrixId = e.matrixId;
          if (matrixId == null) return;

          const matrixRow = matrixRowById.get(matrixId);
          if (!matrixRow) return;

          const agendaItemId = matrixRow.demonstrationAgendaItemId;
          const subcriteriaId = matrixRow.subcriteriaDirectusId;
          if (agendaItemId == null || !subcriteriaId) return;

          const key = `${agendaItemId}-${subcriteriaId}`;
          if (e.rating != null) {
            ratingsMap.set(key, e.rating);
          }
        });
      }
    }

    this.userRatings.set(ratingsMap);

    // Comment icon should indicate whether ANYONE has commented on this agenda item for this tool,
    // regardless of the currently selected user / average mode.
    (data.comments ?? [])
      .filter((c: AgendaItemCommentResponse) => c.participatingToolId === tool.id)
      .forEach((c: AgendaItemCommentResponse) => {
        if (c.demonstrationAgendaItemId == null) return;
        commentSet.add(c.demonstrationAgendaItemId);
      });
    this.hasComments.set(commentSet);
  }

  getRating(agendaItemId: number, subcriteriaId: string): number | undefined {
    const key = `${agendaItemId}-${subcriteriaId}`;
    return this.userRatings().get(key);
  }

  onRate(agendaItemId: number, subcriteriaId: string, val: number) {
    if (this.readOnly()) return;

    const matrixRow = (this.data().matrixRows ?? []).find(
      (r: EvaluationMatrixRowResponse) =>
        r.demonstrationAgendaItemId === agendaItemId && r.subcriteriaDirectusId === subcriteriaId,
    );

    if (!matrixRow) {
      console.error('Matrix row not found for', agendaItemId, subcriteriaId);
      return;
    }
    if (matrixRow.id == null) {
      console.error('Matrix row missing id for', agendaItemId, subcriteriaId);
      return;
    }

    this.ratingChange.emit({
      matrixId: matrixRow.id,
      participatingToolId: this.tool().id,
      rating: val,
    });

    const key = `${agendaItemId}-${subcriteriaId}`;
    const newMap = new Map(this.userRatings());
    newMap.set(key, val);
    this.userRatings.set(newMap);
  }

  openComment(agendaItemId: number) {
    this.commentOpen.emit(agendaItemId);
  }

  hasComment(agendaItemId: number): boolean {
    return this.hasComments().has(agendaItemId);
  }
}
